import { useHierarchyArea } from '@plarium/hierarchy-react';
import React from 'react';

import isArray from 'lodash/isArray';

import { Grid, TextField } from '@gogame/components';
import {
  FormFieldGroup,
  FormField,
  withArrayFormField,
  withFormField,
} from '@gogame/form';

import InfoPopover from 'scenes/Campaigns/components/Form/Link/InfoPopover';
import { loadGGLinks } from 'scenes/Campaigns/sources/fields';

import {
  deepLinkSchema,
  DEEP_LINK_LOCATION_IN_GAME,
  DEEP_LINK_MULTI_REDEMPTIONS,
  DEEP_LINK_REWARD_PACKAGES,
  DEEP_LINK_TARGET_USERS,
  DEEP_LINK_TARGET_USERS_AUDIENCE,
  DEEP_LINK_TARGET_USERS_ALL_USERS,
} from '../DeepLink';
import {
  CAMPAIGNS,
  DEEP_LINK,
  HYBRID_LINK,
  CTV_LINK,
  AGENCY_LINK,
  PARTNER_NAME,
  ADDITIONAL_PARAMS,
  ADDITIONAL_PARAMS_FIELD,
  ANDROID_ADDITIONAL_PARAMS_FIELD,
  IOS_ADDITIONAL_PARAMS_FIELD,
  REWARD_PACKAGE,
  ADDITIONAL_REWARD_PACKAGE,
  ADDITIONAL_REWARD_CONDITION,
  APPLICATION,
  AFFILIATE,
  CPP_PARAMS,
  CONVERSION_PAGE_ID,
  TRACKING_TYPE,
  DEEP_TYPE,
  HYBRYD_TYPE,
  CTV_TYPE,
  AGENCY_TYPE,
} from '../constants';
import {
  DeepLinkRewardPackagesField,
  DeepLinkEndDateField,
  DeepLinkLocationField,
  DeepLinkSubLocationField,
  DeepLinkTargetUsersField,
  SelectedDateField,
  PartnerNameField,
  DeepLinkMultipleRedemptions,
  DeepLinkMaxRedemptions,
  DeepLinkAudiencesField,
} from '../fields';
import { DeepLinkSection } from './DeepLinkSection';

const IOS = 2;
const ANDROID = 3;

const CustomPartnerNameField = withFormField({
  padding: 0,
  validate: ([value]) => {
    return value?.[AGENCY_LINK]?.[PARTNER_NAME] ? '' : 'Can`t be empty';
  },
  name: CAMPAIGNS,
})(({ label = 'Partner Name', form, name, value, disabled, ...other }) => {
  const handlePartnerNameChange = (_e, val) => {
    form.setValues({
      ...form.values,

      [name]: value.map((campaign) => ({
        ...campaign,
        [AGENCY_LINK]: {
          ...campaign[AGENCY_LINK],
          [PARTNER_NAME]: val,
        },
      })),
    });
  };

  return (
    <TextField
      value={value?.[0]?.[AGENCY_LINK]?.[PARTNER_NAME]}
      padding={0}
      label={label}
      name={PARTNER_NAME}
      debounceTimeout={300}
      onChange={handlePartnerNameChange}
      required={true}
      disabled={disabled}
      error={isArray(other?.error) ? false : other?.error}
    />
  );
});

const commonFields = [
  APPLICATION,
  AFFILIATE,
  REWARD_PACKAGE,
  ADDITIONAL_REWARD_PACKAGE,
  ADDITIONAL_REWARD_CONDITION,
  CONVERSION_PAGE_ID,
  CPP_PARAMS,
  CAMPAIGNS,
  HYBRID_LINK,
  CTV_LINK,
  AGENCY_LINK,
];

const singleParamsFunction = () => (values) => {
  try {
    const p = {
      linkTypeId: values?.linkTypeId,
      applicationId: values?.applicationId,
      affiliateId: values?.affiliateId,
      networkId: values?.networkId,

      rewardPackageId: values?.rewardPackageId,
      additionalRewardPackageId: values?.additionalRewardPackageId,
      additionalRewardConditionId: values?.additionalRewardConditionId,

      hybridLink: values?.hybridLink,
      ctvLink: values?.ctvLink,
      agencyLink: values?.agencyLink,

      conversionPageIds: values?.conversionPageIds,

      ...(values?.platformId === ANDROID && {
        androidCpp: values?.customProductPageParams,
      }),
      ...(values?.platformId === IOS && {
        iosCpp: values?.customProductPageParams,
      }),

      ...(values?.platformId === ANDROID && {
        androidConversionPageId: values?.conversionPageId,
      }),
      ...(values?.platformId === IOS && {
        iosConversionPageId: values?.conversionPageId,
      }),

      platformId: values?.platformId,
      conversionPageId: values?.conversionPageId,
      additionalParams: values?.additionalParams,
    };

    return p;
  } catch (error) {
    /* eslint-disable-next-line */
    console.log('debug params', error);
  }
};

const hybridParamsFunction = () => (values) => {
  try {
    const p = {
      linkTypeId: values?.linkTypeId,
      applicationId: values?.applicationId,
      affiliateId: values?.affiliateId,
      networkId: values?.networkId,
      rewardPackageId: values?.rewardPackageId,
      additionalRewardPackageId: values?.additionalRewardPackageId,
      additionalRewardConditionId: values?.additionalRewardConditionId,
      hybridLink: values?.hybridLink,
      platformId: values?.platformId,

      androidConversionPageId: values?.campaigns[0]?.conversionPageId || null,
      iosConversionPageId: values?.campaigns[1]?.conversionPageId || null,

      androidCpp: values?.campaigns[0]?.customProductPageParams || null,
      iosCpp: values?.campaigns[1]?.customProductPageParams || null,
    };

    return p;
  } catch (error) {
    /* eslint-disable-next-line */
    console.log('debug params', error);
  }
};

const ctvParamsFunction = () => (values) => {
  try {
    const p = {
      linkTypeId: values?.linkTypeId,
      applicationId: values?.applicationId,
      affiliateId: values?.affiliateId,
      networkId: values?.networkId,
      rewardPackageId: values?.rewardPackageId,
      additionalRewardPackageId: values?.additionalRewardPackageId,
      additionalRewardConditionId: values?.additionalRewardConditionId,
      ctvLink: values?.ctvLink,
      platformId: values?.platformId,
    };

    return p;
  } catch (error) {
    /* eslint-disable-next-line */
    console.log('debug params', error);
  }
};

const paramsFunction = (index) => (values) => {
  try {
    const p = {
      linkTypeId: values?.linkTypeId,
      applicationId: values?.applicationId,
      affiliateId: values?.affiliateId,
      networkId: values?.networkId,

      rewardPackageId: values?.rewardPackageId,
      additionalRewardPackageId: values?.additionalRewardPackageId,
      additionalRewardConditionId: values?.additionalRewardConditionId,

      platformId: values?.campaigns[index].platformId,
      additionalParams: values?.campaigns[index].link?.additionalParams,

      conversionPageIds: values?.conversionPageIds,

      androidConversionPageId:
        values?.campaigns[index].platformId === ANDROID
          ? values?.campaigns[index].conversionPageId
          : null,
      iosConversionPageId:
        values?.campaigns[index].platformId === IOS
          ? values?.campaigns[index].conversionPageId
          : null,

      androidCpp:
        values?.campaigns[index].platformId === ANDROID
          ? values?.campaigns[index].customProductPageParams
          : null,
      iosCpp:
        values?.campaigns[index].platformId === IOS
          ? values?.campaigns[index].customProductPageParams
          : null,
    };

    return p;
  } catch (error) {
    /* eslint-disable-next-line */
    console.log('debug params', error);
  }
};

const agencyParamsFunction = (index) => (values) => {
  try {
    const p = {
      linkTypeId: values?.linkTypeId,
      applicationId: values?.applicationId,
      affiliateId: values?.affiliateId,
      networkId: values?.networkId,
      rewardPackageId: values?.rewardPackageId,
      additionalRewardPackageId: values?.additionalRewardPackageId,
      additionalRewardConditionId: values?.additionalRewardConditionId,
      platformId: values?.campaigns[index].platformId,

      agencyLink: values?.campaigns[index].agencyLink,
    };

    return p;
  } catch (error) {
    /* eslint-disable-next-line */
    console.log('debug params', error);
  }
};

const BH = ({ paramsFn, name, index = 0 }) => {
  const { data } = useHierarchyArea({
    name: name,
    source: loadGGLinks,
    depends: commonFields,
    params: paramsFn(index),
  });
  return <InfoPopover data={data} />;
};

const TrackingLinkPreviewComponent = (name, index) => (
  <BH name={name} deps={commonFields} paramsFn={paramsFunction} index={index} />
);

const HybridLinkPreviewComponent = () => (
  <BH name={'hybrid'} deps={commonFields} paramsFn={hybridParamsFunction} />
);

const CTVLinkPreviewComponent = () => (
  <BH name={'ctv'} deps={commonFields} paramsFn={ctvParamsFunction} />
);

const AgencyLinkPreviewComponent = (name, index) => (
  <BH
    name={name}
    deps={commonFields}
    paramsFn={agencyParamsFunction}
    index={index}
  />
);

const SingleLinkPreviewComponent = () => (
  <BH name={'single'} deps={commonFields} paramsFn={singleParamsFunction} />
);

const AdditionalParamsField = ({
  label = 'Additional Parameters',
  disabled,
  previewComponent,
}) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={11}>
        <FormField.TextField
          padding={0}
          label={label}
          name={ADDITIONAL_PARAMS}
          placeholder={'&parameter=value'}
          debounceTimeout={300}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={1}>
        {previewComponent()}
      </Grid>
    </Grid>
  );
};

const MultiPlatformAdditionalParamsFields = withArrayFormField()(({
  name,
  value,
  link = 'link',
  fields,
  previewComponent,
  disabled,
}) => {
  return (
    <Grid container item spacing={2}>
      {value?.map((campaign, i) => {
        let label;
        let field;

        switch (campaign.platformId) {
          case ANDROID:
            label = 'Android';
            field = ANDROID_ADDITIONAL_PARAMS_FIELD;
            break;
          case IOS:
            label = 'IOS';
            field = IOS_ADDITIONAL_PARAMS_FIELD;
            break;
          default:
            label = '';
            field = ADDITIONAL_PARAMS_FIELD;
        }

        const isFieldVisible = fields[field];
        const linkGroupName = `${name}[${i}].${link}`;
        return (
          isFieldVisible && (
            <Grid item xs={12} key={i}>
              <FormFieldGroup name={linkGroupName}>
                <AdditionalParamsField
                  label={`${label} Additional Parameters`}
                  index={i}
                  previewComponent={() =>
                    previewComponent(`${linkGroupName}.preview`, i)
                  }
                  disabled={disabled}
                />
              </FormFieldGroup>
            </Grid>
          )
        );
      })}
    </Grid>
  );
});

const GIFT_ID = 50000;

const TrackerLinksSection = ({ additionalParams, selectedDate }) => {
  return (
    <Grid container spacing={2}>
      <Grid item container xs={12} spacing={2}>
        {additionalParams}
      </Grid>
      <Grid item xs={4}>
        {selectedDate}
      </Grid>
    </Grid>
  );
};

const DeepFields = ({ disabled, withParentCampaign }) => {
  return (
    <Grid container alignItems={'flex-start'} spacing={3}>
      <Grid item xs={12}>
        <FormFieldGroup name={DEEP_LINK} validationSchema={deepLinkSchema}>
          {({ value }) => {
            const multipleRedemptionsDisabled =
              !value?.[DEEP_LINK_REWARD_PACKAGES]?.length ||
              value?.[DEEP_LINK_TARGET_USERS] ===
                DEEP_LINK_TARGET_USERS_ALL_USERS ||
              value?.[DEEP_LINK_TARGET_USERS] ===
                DEEP_LINK_TARGET_USERS_AUDIENCE;

            const isShowAudiences =
              value?.[DEEP_LINK_TARGET_USERS] ===
              DEEP_LINK_TARGET_USERS_AUDIENCE;

            const isGiftLocation =
              value?.[DEEP_LINK_LOCATION_IN_GAME] === GIFT_ID;

            const targetUserValidation = (v) => {
              if (isGiftLocation && (v === undefined || v === '')) {
                return 'Target user is ';
              }
            };

            const isShowTargetUsers =
              value?.[DEEP_LINK_REWARD_PACKAGES]?.length > 0;

            return (
              <DeepLinkSection
                rewardPackage={
                  <DeepLinkRewardPackagesField disabled={disabled} />
                }
                location={<DeepLinkLocationField disabled={disabled} />}
                subLocation={
                  <DeepLinkSubLocationField
                    locationInGame={value?.[DEEP_LINK_LOCATION_IN_GAME]}
                    disabled={disabled}
                  />
                }
                multipleRedemptions={
                  <DeepLinkMultipleRedemptions
                    disabled={multipleRedemptionsDisabled}
                  />
                }
                maxRedemptions={
                  value?.[DEEP_LINK_MULTI_REDEMPTIONS] ? (
                    <DeepLinkMaxRedemptions />
                  ) : null
                }
                endDate={<DeepLinkEndDateField disabled={withParentCampaign} />}
                targetUsers={
                  isShowTargetUsers ? (
                    <DeepLinkTargetUsersField
                      disabled={disabled}
                      validate={targetUserValidation}
                      required={isGiftLocation}
                    />
                  ) : null
                }
                audiences={
                  isShowAudiences ? (
                    <DeepLinkAudiencesField disabled={disabled} />
                  ) : null
                }
              />
            );
          }}
        </FormFieldGroup>
      </Grid>
    </Grid>
  );
};

const HybridLinkSection = ({ additionalParams }) => {
  return (
    <Grid item container xs={12}>
      {additionalParams}
    </Grid>
  );
};

const CTVLinkSection = ({ additionalParams }) => {
  return (
    <Grid item container xs={12}>
      {additionalParams}
    </Grid>
  );
};

const AgencyLinksection = ({ partnerName, additionalParams }) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        {partnerName}
      </Grid>
      <Grid item container xs={12}>
        {additionalParams}
      </Grid>
    </Grid>
  );
};

export const SingleLinkFields = ({
  linkFormatType,
  disabled = true,
  withParentCampaign,
  fields,
}) => {
  switch (linkFormatType) {
    case TRACKING_TYPE:
      const isFieldVisible = fields[ADDITIONAL_PARAMS_FIELD];

      return (
        <TrackerLinksSection
          selectedDate={<SelectedDateField disabled={withParentCampaign} />}
          additionalParams={
            isFieldVisible ? (
              <Grid item xs={12}>
                <FormFieldGroup name={'link'}>
                  <AdditionalParamsField
                    previewComponent={SingleLinkPreviewComponent}
                    disabled={disabled}
                  />
                </FormFieldGroup>
              </Grid>
            ) : null
          }
        />
      );
    case DEEP_TYPE:
      return (
        <DeepFields
          disabled={disabled}
          withParentCampaign={withParentCampaign}
        />
      );
    case HYBRYD_TYPE:
      return (
        <FormFieldGroup name={HYBRID_LINK}>
          <HybridLinkSection
            additionalParams={
              <AdditionalParamsField
                previewComponent={SingleLinkPreviewComponent}
                disabled={disabled}
              />
            }
          />
        </FormFieldGroup>
      );
    case CTV_TYPE:
      return (
        <FormFieldGroup name={CTV_LINK}>
          <CTVLinkSection
            additionalParams={
              <AdditionalParamsField
                previewComponent={SingleLinkPreviewComponent}
                disabled={disabled}
              />
            }
          />
        </FormFieldGroup>
      );
    case AGENCY_TYPE:
      return (
        <FormFieldGroup name={AGENCY_LINK}>
          <AgencyLinksection
            partnerName={<PartnerNameField disabled={disabled} />}
            additionalParams={
              <AdditionalParamsField
                previewComponent={SingleLinkPreviewComponent}
                disabled={disabled}
              />
            }
          />
        </FormFieldGroup>
      );
    default:
      return null;
  }
};

export const LinkFields = ({ linkFormatType, fields, disabled }) => {
  switch (linkFormatType) {
    case TRACKING_TYPE:
      return (
        <TrackerLinksSection
          selectedDate={<SelectedDateField />}
          additionalParams={
            <MultiPlatformAdditionalParamsFields
              name={CAMPAIGNS}
              fields={fields}
              previewComponent={TrackingLinkPreviewComponent}
              disabled={disabled}
            />
          }
        />
      );
    case DEEP_TYPE:
      return <DeepFields disabled={disabled} />;
    case HYBRYD_TYPE:
      return (
        <FormFieldGroup name={HYBRID_LINK}>
          <HybridLinkSection
            additionalParams={
              <AdditionalParamsField
                previewComponent={HybridLinkPreviewComponent}
                disabled={disabled}
              />
            }
          />
        </FormFieldGroup>
      );
    case CTV_TYPE:
      return (
        <FormFieldGroup name={CTV_LINK}>
          <CTVLinkSection
            additionalParams={
              <AdditionalParamsField
                previewComponent={CTVLinkPreviewComponent}
                disabled={disabled}
              />
            }
          />
        </FormFieldGroup>
      );
    case AGENCY_TYPE:
      return (
        <AgencyLinksection
          partnerName={<CustomPartnerNameField disabled={disabled} />}
          additionalParams={
            <MultiPlatformAdditionalParamsFields
              name={CAMPAIGNS}
              link={AGENCY_LINK}
              fields={fields}
              previewComponent={AgencyLinkPreviewComponent}
              disabled={disabled}
            />
          }
        />
      );
    default:
      return null;
  }
};
